import React, { useState, useEffect } from 'react'
import {
  ProxySearchResult,
  ProxyMode,
} from '../../../../components/common/config/adminConfig'
import Table from '../../../../components/common/Table/Table'



interface Props {
  results?: ProxySearchResult[]
  onEdit: (row: ProxySearchResult) => void
  mode?: ProxyMode
}

/**
 * Formats a semicolon-delimited proxy list into a newline-joined string so
 * each proxy renders on its own line. Both the requestor (`reqstorProxy`) and
 * approver (`approverProxy`, "; "-joined) APIs return this shape.
 *
 * @param value - Raw semicolon-delimited proxy list from the search API.
 * @returns Newline-joined proxy entries, or '' when there is nothing to show.
 */
const formatSemicolonList = (value?: string): string =>
  value
    ? value
        .split(';')
        .map((p) => p.trim())
        .filter(Boolean)
        .join('\n')
    : ''

/**
 * Resolves the proxy-column text for a result row based on the active mode.
 * The requestor list is preferred when present; otherwise a mode-specific
 * fallback chain is used. Approver mode splits the "; "-joined proxy list so
 * each proxy renders on its own line.
 *
 * @param r - The result row.
 * @param mode - Active proxy mode.
 * @returns Display string for the proxy column ('-' when nothing is available).
 */
const getProxyDisplay = (r: ProxySearchResult, mode: ProxyMode): string => {
  const requestorList = formatSemicolonList(r.reqstorProxy)
  if (requestorList) return requestorList

  if (mode === 'approver') {
    return (
      formatSemicolonList(r.approverProxy) ||
      r.requestorNuid ||
      r.floatEmployee ||
      r.nuid ||
      'None Assigned.'
    )
  }
  if (mode === 'requestor') {
    return (
      r.requestorNuid ?? r.approverProxy ?? r.floatEmployee ?? r.nuid ?? '-'
    )
  }
  if (mode === 'float') {
    // Show "None Assigned." when no float employee is assigned (empty string or missing)
    return r.floatEmployee || 'None Assigned.'
  }
  return r.floatEmployee ?? r.requestorNuid ?? r.approverProxy ?? r.nuid ?? '-'
}

const ProxySearchResultsTable: React.FC<Props> = ({
  results,
  onEdit,
  mode = 'approver',
}) => {
    const [editingRowId, setEditingRowId] = useState<string | null>(null)

  // Reset the "Editing" indicator whenever a new search result set arrives so
  // a previously-selected row does not stay highlighted after the same-param re-search.
  useEffect(() => {
    setEditingRowId(null)
  }, [results])

  // if `results` is undefined, no search has been performed yet — render nothing
  if (results === undefined) return null


  const data = results || []

  // Only treat items with a groupId as group rows
  const groups = data.filter((d) => !!d.groupId)
  // Proxy column label varies by mode
  let proxyColumnLabel = 'Approver Proxy (NUID)'

  if (mode === 'requestor') {
    proxyColumnLabel = 'Requestor Proxy (NUID)'
  } else if (mode === 'float') {
    proxyColumnLabel = 'Float Employee (NUID)'
  }

  const groupColumns = [
    { key: 'groupId', label: 'Approval Group ID', className: 'align-middle! md:!min-w-[140px]' },
    ...(mode === 'float' || mode === 'approver' || mode === 'requestor'
      ? [{ key: 'region', label: 'Region', className: 'align-middle! md:!min-w-[100px]' }]
      : []),
    { key: 'department', label: 'Department', className: 'align-middle! md:!min-w-[140px] tablet:max-w-[250px] desktop:max-w-none' },
    {
      key: 'primaryNuid',
      label: 'Primary Approver (NUID)',
      className: 'align-middle! md:!min-w-[140px]',
    },
    { key: 'proxyNuid', label: proxyColumnLabel, className: 'align-middle! md:!min-w-[140px] tablet:max-w-[250px] desktop:max-w-[300px]' },
    { key: 'action', label: 'Edit', className: 'text-right align-middle! md:!min-w-[80px] tablet:whitespace-nowrap tablet:min-w-[80px]' },
  ]

  const groupRows = groups.map((r) => {
    // Prefer new API response fields; fall back to legacy mock fields
    const displayGroupId = r.approvalGrpId ?? r.groupId ?? r.id ?? '-'
    const displayApprover = r.approver ?? r.primaryNuid ?? r.nuid ?? ''

    // Proxy column is a semicolon-delimited list rendered one entry per line
    const proxyDisplay = getProxyDisplay(r, mode)

    return {
      id: String(r.approvalGrpId ?? r.id),
      groupId: <span>{displayGroupId}</span>,
      department: <span className="tablet:block tablet:break-words">{r.department ?? '-'}</span>,
      primaryNuid: <span>{displayApprover}</span>,
      ...(mode === 'float' || mode === 'approver' || mode === 'requestor' ? { region: <span>{r.region ?? '-'}</span> } : {}),
      proxyNuid: <span className="tablet:block tablet:break-words" style={{ whiteSpace: 'pre-line' }}>{proxyDisplay}</span>,
action: (
  <div className="flex items-center gap-2 tablet:justify-end tablet:whitespace-nowrap">
    <img
      src="/assets/images/pencil-outline.svg"
      alt="Edit"
      className="w-4 h-4 shrink-0"
    />
    <span style={{ color: '#0074AD' }} className="shrink-0">
      {editingRowId === String(r.approvalGrpId ?? r.id)
        ? 'Editing'
        : 'Edit'}
    </span>
  </div>
),
    }
  })

  if (data.length === 0) {
    return (
      <div role="alert" className="mt-8! p4">
        <div className="flex items-center gap-3">
          <img
            src="/assets/images/alertSolid.svg"
            alt="Alert"
            className="w-8! h-8! shrink-0"
          />
          <div className="h3">No Results Found</div>
        </div>
        <div className="p3 -book mt-2! ml-10!">Please modify your search</div>
      </div>
    )
  }

  let headerText = 'Select an approver or group to assign an Approver Proxy.'
  if (mode === 'requestor')
    headerText = 'Select an approver or group to assign a Requestor Proxy.'
  else if (mode === 'float')
    headerText = 'Select an approver or group to assign a Float Employee.'

  return (
    <div>
      <div className="p3 -medium mb-2 mt-4!">
        {data.length === 1
          ? '1 Approver Group found'
          : `${data.length} Approver Groups found`}
      </div>

      <p className="p3 mb-4">{headerText}</p>

      <div>
        {/* Scrollable wrapper: when there are more than 10 rows the container
            caps the visible height so the user can scroll to see the rest. */}
        <div
          className={
            data.length > 10 ? 'max-h-[700px] overflow-auto' : undefined
          }
        >
          <Table
            columns={groupColumns}
            rows={groupRows}
            stickyHeader={data.length > 10}
            onActionClick={(rowData) => {
              if (!rowData || typeof rowData !== 'object' || !('id' in rowData))
                return
              const idValue = (rowData as Record<string, unknown>).id
              // Guard against non-primitive values to avoid '[object Object]' stringification
              const rowId =
                typeof idValue === 'string' || typeof idValue === 'number'
                  ? String(idValue)
                  : ''
              // Match on approvalGrpId first (new API), fall back to id (legacy)
              const found = groups.find(
                (r) => String(r.approvalGrpId ?? r.id) === rowId
              )
 if (found) {
  setEditingRowId(String(found.approvalGrpId ?? found.id))
  onEdit(found)
}
            }}
            showPagination={false}
          />
        </div>
      </div>
    </div>
  )
}

export default ProxySearchResultsTable
